'use client';

import React, { useState } from 'react';
import { Car, CheckCircle2, MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { safeHref } from '@/lib/storefront/safe-url';
import { resolveContactField, useStorefrontData } from '@/lib/storefront/storefront-data';
import { useLeadSubmit } from '@/lib/storefront/use-lead-form';
import { apiAsset } from '@/config';
import { containerClass, sectionStyle } from './HeaderSections';

const SOCIAL_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
};

const defaultColumns = [
  {
    title: 'Company',
    links: [
      { label: 'About us', href: '/about' },
      { label: 'Careers', href: '/about' },
      { label: 'Press', href: '/about' },
      { label: 'Contact', href: '/contact' },
    ],
  },
  {
    title: 'Rentals',
    links: [
      { label: 'Our fleet', href: '/vehicles' },
      { label: 'Long-term rental', href: '/vehicles' },
      { label: 'Corporate accounts', href: '/contact' },
      { label: 'Airport pick-up', href: '/locations' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Help centre', href: '/faq' },
      { label: 'Rental terms', href: '/legal/terms' },
      { label: 'Insurance', href: '/faq' },
      { label: 'FAQ', href: '/faq' },
    ],
  },
];

// ──────────────────────────────────────────────────────────────
// 1. Multi-column footer
// ──────────────────────────────────────────────────────────────

export function FooterColumns({ content, style }: SectionProps) {
  const { agency } = useStorefrontData();
  const {
    description = 'Car rental along Morocco’s Atlantic coast. A modern fleet, honest prices and people who pick up the phone.',
    columns = defaultColumns,
    socials = [
      { network: 'facebook', href: 'https://facebook.com' },
      { network: 'instagram', href: 'https://instagram.com' },
      { network: 'linkedin', href: 'https://linkedin.com' },
    ],
  } = content;
  const logoText = resolveContactField(content.logoText, agency?.name) || 'Agency';
  const logoUrl = resolveContactField(content.logoUrl, agency?.logoUrl);
  const phone = resolveContactField(content.phone, agency?.phone);
  const email = resolveContactField(content.email, agency?.email);
  const address = resolveContactField(content.address, agency?.address);
  const legal = resolveContactField(
    content.legal,
    agency?.name ? `© ${new Date().getFullYear()} ${agency.name}. All rights reserved.` : undefined,
  );

  return (
    <footer className="w-full bg-slate-950 text-slate-300" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="grid gap-10 py-4 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <a href="/" className="flex items-center gap-2 text-xl font-bold text-white">
              {logoUrl ? (
                <img src={apiAsset(logoUrl)} alt="" className="h-8 w-auto max-w-[9rem] object-contain" />
              ) : (
                <Car className="h-6 w-6 text-brand" />
              )}
              {logoText}
            </a>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">{description}</p>

            <div className="mt-5 space-y-2 text-sm text-slate-400">
              {phone && (
                <a href={`tel:${String(phone).replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-white">
                  <Phone className="h-4 w-4" /> {phone}
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-white">
                  <Mail className="h-4 w-4" /> {email}
                </a>
              )}
              {address && (
                <p className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {address}
                </p>
              )}
            </div>
          </div>

          {columns.map((col: any, i: number) => (
            <div key={i}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-white">{col.title}</h3>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l: any, j: number) => (
                  <li key={j}>
                    <a href={safeHref(l.href)} className="text-sm text-slate-400 hover:text-white">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col items-center justify-between gap-4 border-t border-white/10 py-5 sm:flex-row">
          <p className="text-xs text-slate-500">{legal}</p>
          <div className="flex items-center gap-3">
            {socials.map((s: any, i: number) => {
              const Icon = SOCIAL_ICON[s.network] ?? Facebook;
              return (
                <a key={i} href={safeHref(s.href)} className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white" aria-label={s.network}>
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ──────────────────────────────────────────────────────────────
// 2. Simple centered footer
// ──────────────────────────────────────────────────────────────

export function FooterSimple({ content, style }: SectionProps) {
  const { agency } = useStorefrontData();
  const {
    tagline = 'Rentals made simple.',
    links = [
      { label: 'Vehicles', href: '/vehicles' },
      { label: 'Pricing', href: '/deals' },
      { label: 'Contact', href: '/contact' },
      { label: 'Terms', href: '/legal/terms' },
      { label: 'Privacy', href: '/legal/privacy' },
    ],
  } = content;
  const logoText = resolveContactField(content.logoText, agency?.name) || 'Agency';
  const logoUrl = resolveContactField(content.logoUrl, agency?.logoUrl);
  const legal = resolveContactField(
    content.legal,
    agency?.name ? `© ${new Date().getFullYear()} ${agency.name}. All rights reserved.` : undefined,
  );

  return (
    <footer className="w-full border-t border-slate-200 bg-white" style={sectionStyle(style)}>
      <div className={`${containerClass(style)} py-4 text-center`}>
        <a href="/" className="inline-flex items-center gap-2 text-lg font-bold text-slate-900">
          {logoUrl ? (
            <img src={apiAsset(logoUrl)} alt="" className="h-7 w-auto max-w-[8rem] object-contain" />
          ) : (
            <Car className="h-5 w-5 text-brand" />
          )}
          {logoText}
        </a>
        {tagline && <p className="mt-2 text-sm text-slate-500">{tagline}</p>}

        <nav className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          {links.map((l: any, i: number) => (
            <a key={i} href={safeHref(l.href)} className="text-sm text-slate-600 hover:text-brand">
              {l.label}
            </a>
          ))}
        </nav>

        <p className="mt-5 border-t border-slate-100 pt-4 text-xs text-slate-400">{legal}</p>
      </div>
    </footer>
  );
}

// ──────────────────────────────────────────────────────────────
// 3. Footer with newsletter
// ──────────────────────────────────────────────────────────────

export function FooterNewsletter({ content, style }: SectionProps) {
  const { agency } = useStorefrontData();
  const {
    newsletterTitle = 'Deals in your inbox',
    newsletterText = 'One short email a month with seasonal rates and last-minute availability. No spam.',
    placeholder = 'your@email.com',
    buttonText = 'Subscribe',
    newsletterSuccess = 'You are subscribed. Watch your inbox.',
    columns = defaultColumns,
  } = content;
  const logoText = resolveContactField(content.logoText, agency?.name) || 'Agency';
  const legal = resolveContactField(
    content.legal,
    agency?.name ? `© ${new Date().getFullYear()} ${agency.name}. All rights reserved.` : undefined,
  );
  const { pending, done, error, submit } = useLeadSubmit('newsletter', 'footer');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await submit({ email: email.trim(), website });
  };

  return (
    <footer className="w-full bg-slate-900 text-slate-300" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="grid items-center gap-6 border-b border-white/10 py-8 md:grid-cols-2">
          <div>
            <h3 className="text-lg font-semibold text-white">{newsletterTitle}</h3>
            <p className="mt-1.5 text-sm text-slate-400">{newsletterText}</p>
          </div>
          <div className="w-full md:justify-self-end">
            {done ? (
              <p className="flex items-center gap-2 text-sm font-semibold text-emerald-400 md:justify-end">
                <CheckCircle2 className="h-4 w-4" />
                {newsletterSuccess}
              </p>
            ) : (
              <form
                className="flex w-full gap-2 md:justify-end"
                onSubmit={handleSubmit}
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={placeholder}
                  className="w-full max-w-xs rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-brand"
                />
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />
                <button
                  type="submit"
                  disabled={pending}
                  className="shrink-0 rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {pending ? 'Sending…' : buttonText}
                </button>
              </form>
            )}
            {error ? <p className="mt-2 text-xs font-semibold text-rose-400 md:text-right">{error}</p> : null}
          </div>
        </div>

        <div className="grid gap-8 py-8 md:grid-cols-4">
          <div>
            <a href="/" className="flex items-center gap-2 text-lg font-bold text-white">
              <Car className="h-5 w-5 text-brand" />
              {logoText}
            </a>
          </div>
          {columns.map((col: any, i: number) => (
            <div key={i}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-white">{col.title}</h4>
              <ul className="mt-3 space-y-2">
                {col.links.map((l: any, j: number) => (
                  <li key={j}>
                    <a href={safeHref(l.href)} className="text-sm text-slate-400 hover:text-white">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 py-4 text-center text-xs text-slate-500">{legal}</div>
      </div>
    </footer>
  );
}

export const FOOTER_VARIATIONS = {
  columns: FooterColumns,
  simple: FooterSimple,
  newsletter: FooterNewsletter,
} as const;

export type FooterVariationKey = keyof typeof FOOTER_VARIATIONS;
